import localforage from 'localforage';

/**
 * ImageCacheService: High-performance IndexedDB storage for local image blobs.
 * Part of the Sovereign Protocol to prevent data loss on refresh without filling localStorage.
 */

// Initialize a dedicated store for the CartLens basket images
const basketImageStore = localforage.createInstance({
  name: "cartlens",
  storeName: "basket_images",
  description: "Temporary local storage for scanned items in the basket"
});

// Initialize a persistent registry for mapping local IDs to cloud URLs
// This ensures chat history can resolve images after local cleanup
const referenceRegistry = localforage.createInstance({
  name: "cartlens",
  storeName: "image_references",
  description: "Permanent mapping of local IDs to cloud URLs"
});

// Dedicated store for chat message images — intentionally NOT cleared on basket clear/archive.
// These images use the "chat:" prefix and must persist across basket lifecycle events.
const chatImageStore = localforage.createInstance({
  name: "cartlens",
  storeName: "chat_images",
  description: "Persistent storage for images attached to chat messages"
});

// Per-thread last-image registry — survives processing failures so the AI can retry
const threadImageRefStore = localforage.createInstance({
  name: "cartlens",
  storeName: "thread_image_refs",
  description: "Last image uploaded per chat thread, for AI retry without re-upload"
});

// Internal cache to prevent creating multiple blob URLs for the same local ID
const blobUrlMap = new Map<string, string>();

export const ImageCacheService = {
  /**
   * Saves an image blob to the local disk and returns a local URI reference.
   */
  async saveImage(blob: Blob | File): Promise<string> {
    const id = `local:${Date.now()}_${Math.random().toString(36).substring(2, 7)}`;
    try {
      await basketImageStore.setItem(id, blob);
      return id;
    } catch (e) {
      console.error("[ImageCache] Failed to save blob to IndexedDB:", e);
      return URL.createObjectURL(blob);
    }
  },

  /**
   * Registers a permanent cloud URL for a local identifier.
   */
  async registerCloudUrl(localId: string, cloudUrl: string): Promise<void> {
    if (!localId.startsWith('local:') && !localId.startsWith('chat:')) return;
    try {
      await referenceRegistry.setItem(localId, cloudUrl);
    } catch (e) {
      console.error("[ImageCache] Failed to register cloud URL:", e);
    }
  },

  /**
   * Checks if a local ID has a registered cloud URL.
   */
  async getCloudUrl(localId: string): Promise<string | null> {
    if (!localId.startsWith('local:') && !localId.startsWith('chat:')) return null;
    try {
      return await referenceRegistry.getItem(localId);
    } catch (e) {
      return null;
    }
  },

  /**
   * Saves a chat message image to the dedicated chat store.
   * Uses "chat:" prefix — immune to clearAll() basket clears.
   */
  async saveChatImage(blob: Blob | File): Promise<string> {
    const id = `chat:${Date.now()}_${Math.random().toString(36).substring(2, 7)}`;
    try {
      await chatImageStore.setItem(id, blob);
      return id;
    } catch (e) {
      console.error("[ImageCache] Failed to save chat image:", e);
      return URL.createObjectURL(blob);
    }
  },

  /**
   * Retrieves a blob from the appropriate store based on the URL prefix.
   * Handles both "local:" (basket) and "chat:" (chat message) references.
   */
  async getImageBlob(id: string): Promise<Blob | null> {
    try {
      if (id.startsWith('chat:')) {
        return await chatImageStore.getItem<Blob>(id);
      }
      if (id.startsWith('local:')) {
        return await basketImageStore.getItem(id);
      }
      return null;
    } catch (e) {
      console.error("[ImageCache] Failed to retrieve blob:", e);
      return null;
    }
  },

  /**
   * Synchronously checks if a local ID already has an active blob URL in memory.
   */
  getCachedUrl(id: string): string | null {
    if (!id) return null;
    return blobUrlMap.get(id) || null;
  },

  /**
   * Generates a temporary blob URL for the UI to display an image from the cache.
   * Handles both "local:" (basket) and "chat:" (chat message) prefixes.
   * If a cloud URL is registered, it returns that instead.
   */
  async resolveLocalUrl(id: string, force: boolean = false): Promise<string | null> {
    // 1. Check Registry First (Upgraded reference)
    const cloudUrl = await this.getCloudUrl(id);
    if (cloudUrl) return cloudUrl;

    // 2. Check memory map for singleton blob URL
    if (!force && blobUrlMap.has(id)) {
      return blobUrlMap.get(id) || null;
    }

    // 3. Check appropriate store based on prefix
    let blob: Blob | null = null;
    if (id.startsWith('chat:')) {
      blob = await chatImageStore.getItem<Blob>(id);
    } else {
      blob = await this.getImageBlob(id);
    }

    if (blob) {
      const url = URL.createObjectURL(blob);
      blobUrlMap.set(id, url);
      return url;
    }
    return null;
  },

  /**
   * Removes a specific image from the local disk and its reference.
   */
  async removeImage(id: string): Promise<void> {
    if (!id.startsWith('local:')) return;
    try {
      await basketImageStore.removeItem(id);
      await referenceRegistry.removeItem(id); // PIVOT: Also cleanup the reference
      const url = blobUrlMap.get(id);
      if (url) {
        URL.revokeObjectURL(url);
        blobUrlMap.delete(id);
      }
    } catch (e) {
      console.error("[ImageCache] Failed to remove image:", e);
    }
  },

  /**
   * Removes multiple chat images from the local disk.
   */
  async removeChatImages(ids: string[]): Promise<void> {
    const chatIds = ids.filter(id => id.startsWith('chat:'));
    if (chatIds.length === 0) return;
    try {
      await Promise.all(chatIds.map(id => chatImageStore.removeItem(id)));
      chatIds.forEach(id => {
        const url = blobUrlMap.get(id);
        if (url) {
          URL.revokeObjectURL(url);
          blobUrlMap.delete(id);
        }
      });
    } catch (e) {
      console.error("[ImageCache] Failed to remove chat images:", e);
    }
  },

  /**
   * Clears all temporary images and their cloud mapping references from the local disk.
   */
  async clearAll(): Promise<void> {
    try {
      await Promise.all([
        basketImageStore.clear(),
        referenceRegistry.clear() // PIVOT: Clear registry to prevent indefinite growth
      ]);
      // Clear the map without revoking — components may still be displaying these blob URLs.
      // The blobs will be GC'd naturally once no img elements reference them.
      blobUrlMap.clear();
    } catch (e) {
      console.error("[ImageCache] Failed to clear store:", e);
    }
  },

  /**
   * Persists the last uploaded image URL for a given thread.
   * Call this immediately after saveChatImage so the AI can retry on failure.
   */
  async setThreadImageRef(threadId: string, imageUrl: string, filename?: string): Promise<void> {
    if (!threadId || threadId === 'placeholder') return;
    try {
      await threadImageRefStore.setItem(threadId, {
        url: imageUrl,
        filename: filename || 'image.jpg',
        timestamp: Date.now(),
      });
    } catch (e) {
      console.error("[ImageCache] Failed to save thread image ref:", e);
    }
  },

  /**
   * Retrieves the last image ref for a thread, or null if none.
   */
  async getThreadImageRef(threadId: string): Promise<{ url: string; filename: string; timestamp: number } | null> {
    if (!threadId || threadId === 'placeholder') return null;
    try {
      return await threadImageRefStore.getItem(threadId);
    } catch (e) {
      return null;
    }
  },

  /**
   * Clears the image ref for a thread (call after successful processing).
   */
  async clearThreadImageRef(threadId: string): Promise<void> {
    if (!threadId) return;
    try {
      await threadImageRefStore.removeItem(threadId);
    } catch (e) {}
  },

  /**
   * Utility: Check if a string is a local cache reference.
   */
  isLocalReference(url: string | undefined | null): boolean {
    return !!url && url.startsWith('local:');
  }
};
